/*
 * $Id:: CalendarWrapper.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import { TextField } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateRange } from 'react-date-range';
import * as rdrLocales from 'react-date-range/dist/locale';

import ArrowPrevious from '../../assets/images/arrow-back.svg';
import ArrowNext from '../../assets/images/arrow-forward.svg';
import DateArrow from '../../assets/images/date_arrow.svg';
import { getLocale, t } from '../../providers';
import { bareDateTimeFormat } from '../../providers/DateFormatProvider';

const useStyles = makeStyles((theme) => ({
    skipCalendar: {
        position: 'absolute',
        left: '-9999px',
        width: '180px',
        zIndex: '999',
        padding: '4px 8px',
        backgroundColor: '#3F5E20',
        color: 'white',
        fontSize: '0.75rem',
        textDecoration: 'none',
        opacity: '0',
        textTransform: 'uppercase',
        margin: '10px 0 0 0',
        textAlign: 'center',
        boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
        borderRadius: '2px',
        fontWeight: '600',
        lineHeight: '133%',
        letterSpacing: '0.5px',
        '&:focus': {
            transform: 'translateX(62%)',
            opacity: 1,
            border: 'none',
            outline: 'none',
            position: 'unset',
        },
    },
    datepickerContainer: {
        backgroundColor: 'white',
        borderRadius: '4px',
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    // Overriding default theme of datepicker.
    datepicker: {
        '& .rdrDay': {
            height: '3.25em',
            '&:focus-visible': {
                boxSizing: 'border-box',
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
        '& .rdrWeekDays': {
            textTransform: 'capitalize',
        },
        '& .rdrMonthAndYearWrapper': {
            height: 40,
        },
        '& .rdrPprevButton': {
            order: 2,
        },
        '& .rdrNextButton': {
            order: 3,
        },
        '& .rdrMonthAndYearPickers': {
            order: 1,
            justifyContent: 'left',
        },
        '& .rdrMonthPicker, .rdrYearPicker': {
            margin: '0 5px 0px 0px',
        },
        '& .rdrMonthPicker': {
            marginLeft: '23px',
        },
        '& .rdrMonthAndYearPickers select': {
            fontSize: '0.875rem',
            color: '#5D5D5D',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '142%',
            letterSpacing: '-0.2px',
            background: `url(${DateArrow})`,
            textTransform: 'capitalize',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right',
            padding: '10px 30px 10px 0px',
            textAlign: 'left',
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
        '& .rdrNextPrevButton': {
            background: 'none',
            margin: '0 22px 0px 0px',
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
        '& .rdrPprevButton i': {
            border: 'none',
            background: `url(${ArrowPrevious})`,
            width: 18,
            height: 18,
        },
        '& .rdrNextButton i': {
            border: 'none',
            background: `url(${ArrowNext})`,
            width: 18,
            height: 18,
        },
        '& .rdrMonths': {
            '@media (max-width:600px)': {
                flexDirection: 'column',
            },
        },
        '& .rdrMonth': {
            paddingBottom: '10px !important',
            width: '320px',
            paddingLeft: '10px',
            paddingRight: '10px',
            '@media (max-width:600px)': {
                width: '90%',
            },
            '& .rdrDayNumber': {
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: '142%',
                letterSpacing: '-0.2px',
                color: '#303030',
            },
            '& .rdrStartEdge.rdrEndEdge, .rdrStartEdge, .rdrEndEdge, .rdrDayEndOfMonth .rdrInRange, .rdrDayEndOfMonth .rdrStartEdge, .rdrDayEndOfWeek .rdrInRange, .rdrDayEndOfWeek .rdrStartEdge, .rdrDayStartOfMonth .rdrInRange, .rdrDayStartOfWeek .rdrInRange': {
                borderRadius: '90%',
                height: '3em',
                width: '3em',
                top: '54%',
                left: '52%',
                transform: 'translate(-50%, -52%)',
            },
            '& .rdrInRange': {
                borderRadius: '90%',
                height: '3em',
                width: '3em',
                top: '54%',
                left: '52%',
                transform: 'translate(-50%, -52%)',
                backgroundColor: '#D2E8BC',
                color: theme.palette.text.mainGreen,
                '& + span': {
                    '& span': {},
                },
            },
            '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive)': {
                color: theme.palette.text.mainGreen,
            },
            '& .rdrDayToday .rdrDayNumber span:after': {
                width: 0,
            },
            '& .rdrMonthName': {
                display: 'none',
            },
            '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span': {
                color: '#303030',
            },
            '& .rdrWeekDay': {
                fontSize: '0.75rem',
                color: '#586071',
            },
            '& .rdrMonthAndYearPickers select': {
                fontSize: '0.875rem',
                color: '#5D5D5D',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '142%',
                letterSpacing: '-0.2px',
                background: `url(${DateArrow})`,
            },
            '& .rdrMonthAndYearWrapper': {
                display: 'block',
                '& .rdrNextPrevButton': {
                    background: 'none',
                },
                '& .rdrPprevButton i': {
                    borderColor: 'none',
                    background: `url(${ArrowPrevious})`,
                },
            },
        },
    },
    datepickerHeader: {
        display: 'flex',
        flexFlow: 'row nowrap',
    },
    datepickerLeftArrow: {
        width: '20px',
        paddingLeft: '1rem',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
    },
    datepickerRightArrow: {
        width: '20px',
        paddingRight: '1rem',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
    },
    datepickerPreviewDates: {
        paddingLeft: 20,
        paddingBottom: 20,
        flexFlow: 'row nowrap',
    },
    datepickerPreviewDate: {
        padding: '5px 10px',
        float: 'left',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '133%',
        color: '#5D5D5D',
        width: 118,
        height: 34,
        background: '#FAFAFA',
        border: '1px solid #B1B1B1',
        borderRadius: '3px',
        marginRight: '20px',
        '@media (max-width:600px)': {
            width: '107px',
        },
    },
    rangeDateIcon: {
        height: '1rem',
        paddingTop: '1px',
        marginRight: '0.8rem',
        marginLeft: '0.5rem',
        '& svg': {
            width: '1rem',
            height: '1rem',
        },
    },
    datepickerSelectedDate: {
        backgroundColor: '#FAFAFA',
        borderRadius: '0px',
        width: '5.5rem',
        height: '20px',
        marginLeft: '0',
        verticalAlign: 'middle',
        lineHeight: '20px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    textInput: {
        height: '2rem',
        width: 'auto',
        borderRadius: '4px',
        cursor: 'pointer',
        '& .MuiInputBase-root': {
            border: 'none',
            width: '120px',
            height: '24px',
        },
        '& .MuiInputBase-input': {
            textTransform: 'upperCase',
            fontSize: '0.75rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '133%',
            color: '#5D5D5D',
            padding: 0,
            margin: 0,
        },
        '& input[type="date"]::-webkit-inner-spin-button, input[type="date"]::-webkit-calendar-picker-indicator': {
            display: 'none',
            '-webkit-appearance': 'none',
        },
    },
}));

const CalendarWrapper = ({ onUpdateRanges, range, customHeader }) => {
    const classes = useStyles();
    const theme = useTheme();
    const locale = getLocale(); // get current language

    const [dateRange, setDateRange] = useState([{}]);

    useEffect(() => {
        const now = new Date();

        const { startDate, endDate } = range || {};
        const newDateRange = [{ key: 'selection', startDate: now, endDate: now }];
        if (startDate) newDateRange[0].startDate = startDate;
        if (endDate) newDateRange[0].endDate = endDate;
        setDateRange(newDateRange);
    }, [range, setDateRange]);

    const onChangeRanges = (item) => {
        const startDate = document.getElementById('startDate');
        const endDate = document.getElementById('endDate');
        if (!startDate.value) {
            startDate.value = bareDateTimeFormat(item.selection.startDate, 'YY-mm-dd');
        }
        if (!endDate.value && startDate.value !== bareDateTimeFormat(item.selection.endDate, 'YY-mm-dd')) {
            endDate.value = bareDateTimeFormat(item.selection.endDate, 'YY-mm-dd');
        }
        onUpdateRanges({ startDate: item.selection.startDate, endDate: item.selection.endDate });
    };

    const upDateCalendar = () => {
        const startDate = document.getElementById('startDate');
        const endDate = document.getElementById('endDate');
        const startYear = startDate.value.split('-')[0];
        const endYear = endDate.value.split('-')[0];
        const tmpStartDate = new Date(startDate.value);
        const tmpEndDate = new Date(endDate.value);
        if (tmpStartDate instanceof Date && !isNaN(tmpStartDate) && startYear > 1921 && startYear < 2043) {
            const selection = {
                selection: {
                    endDate: tmpEndDate instanceof Date && !isNaN(tmpEndDate) && endYear > 1921 && endYear < 2043 ? tmpEndDate : tmpStartDate,
                    key: 'selection',
                    startDate: tmpStartDate,
                },
            };
            onChangeRanges(selection);
        }
    };

    const focusStartDateInputFirefox = () => {
        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
            setTimeout(() => {
                document.getElementById('startDate').focus();
            }, 1000);
        }
    };

    return (
        <div className={classes.datepickerContainer}>
            {!!customHeader ? customHeader : null}
            <a href="#startDate" className={classes.skipCalendar} onClick={focusStartDateInputFirefox}>
                {t('skip.to.dates')}
            </a>
            <DateRange
                className={classes.datepicker}
                editableDateInputs={true}
                onChange={onChangeRanges}
                moveRangeOnFirstSelection={false}
                showSelectionPreview={false}
                showMonthAndYearPickers={true}
                showDateDisplay={false}
                color={theme.palette.text.tintGreen}
                rangeColors={[theme.palette.text.tintGreen]}
                ranges={dateRange}
                months={1}
                direction={'horizontal'}
                showMonthName={true}
                showPreview={false}
                monthDisplayFormat={'MMMM yyyy'}
                locale={rdrLocales[locale ? locale : 'en']}
                ariaLabels={{
                    monthPicker: t('month.picker.label'),
                    yearPicker: t('year.picker.label'),
                    prevButton: t('previous.button.label'),
                    nextButton: t('next.button.label'),
                }}
            />
            <div className={classes.datepickerPreviewDates}>
                <div className={classes.datepickerPreviewDate}>
                    <span>{t('from')}</span>
                    <div className={classes.datepickerSelectedDate}>
                        <TextField
                            id="startDate"
                            className={classes.textInput}
                            name="startDate"
                            type="date"
                            onChange={upDateCalendar}
                            inputProps={{ 'aria-label': t('enter.start.day') }}
                        />
                    </div>
                </div>
                <div className={classes.datepickerPreviewDate}>
                    <span>{t('to')}</span>
                    <div className={classes.datepickerSelectedDate}>
                        <TextField
                            className={classes.textInput}
                            type="date"
                            id="endDate"
                            name="endDate"
                            onChange={upDateCalendar}
                            inputProps={{ 'aria-label': t('enter.end.day') }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarWrapper;
